// Registry ORDER — the one rule every currency picker in the console shares.
//
// Its own module rather than a member of CurrencyPresentationProvider: it calls
// `useCurrencyRegistryRows`, and a hook that calls a sibling in the same module
// cannot be given a test double for that sibling (an intra-module call does not
// go through the module's exports, so `vi.mock` never sees it). Importing it
// keeps every picker's registry read mockable the way the money-form tests
// already mock it.

import { useMemo } from "react";

import { useCurrencyRegistryRows } from "@/components/currency/CurrencyPresentationProvider";
import { isCurrencyCode, type CurrencyCode } from "@/lib/currency";

/**
 * `allowed`, in the order the operator arranged the registry (`sort_order`, the
 * field the /currencies page exists to set).
 *
 * The registry is a NARROWING, never a widening. A row for a code the contract
 * does not know is skipped — the same rule `toPresentationSettings` applies —
 * so a bad row upstream cannot introduce an unrenderable option.
 *
 * When the registry is unreadable (loading, no CURRENCY_VIEW, failed read) the
 * caller's own order stands in, and so it does when the registry excludes
 * everything the caller allows. Returning nothing would be the worse failure by
 * far: an empty required picker cannot be used at all, which turns a cosmetic
 * read into a dead control on a page that has otherwise never needed the
 * registry.
 */
export function useRegistryOrderedCurrencies(
  allowed: readonly CurrencyCode[],
): CurrencyCode[] {
  const rows = useCurrencyRegistryRows();
  return useMemo(() => {
    const list = [...allowed];
    if (!rows?.length) return list;
    const offered = rows
      .filter((r) => isCurrencyCode(r.code))
      .slice()
      .sort((a, b) => a.sort_order - b.sort_order)
      .map((r) => r.code as CurrencyCode)
      .filter((c) => list.includes(c));
    return offered.length > 0 ? offered : list;
  }, [rows, allowed]);
}
